import { OfferValue } from '../../models'
import { CartItems, cartItemsNfc } from './CartItems'
import { DiscountCodes } from './DiscountCodes'
import { getLocalStorageObject, setLocalStorageObject } from 'app-logic/storage'
import { CartInfo } from './CartInfo'
import { CartItemSlot } from './CartItemSlot'
import { CartItem } from './CartItem'
import { CartItemSet } from './CartItemSet'
import { Shipping1295, Shipping95 } from './ShippingSet'

export interface UpgradeItemSet {
  itemSet: CartItemSet
  subTitle: string
  title: string
}

export class Cart {
  private constructor() {
    this.data = {}
    this.load()
    this.updateTotals()
    this.updateShipping()
  }

  public static defaultItems() {
    return cartItemsNfc
  }

  public data: CartInfo
  public suggestedEmail?: string
  public items = new Map<CartItemSlot, CartItem>()

  public getItemSet() {
    return (this.data.itemSet = this.data.itemSet ?? CartItemSet.Nfc)
  }

  public save() {
    setLocalStorageObject('cartInfo', this.data)
  }

  public load() {
    const data = getLocalStorageObject<CartInfo>('cartInfo')
    if (!data?.itemSet || !CartItems.get(data?.itemSet)) {
      this.clear()
    } else {
      this.data = data
    }
    if (this.items.size === 0) {
      this.loadDefaultItems()
    }
    return this.data.itemSet
  }

  private refreshDataItems() {
    this.data.items = Array.from(this.items.values()).map((item) => {
      if (item.planId === Shipping95.planId) return Shipping1295
      return item
    })
  }

  public loadDefaultItems() {
    const values = this.getSetItems()?.entries()
    if (values) {
      Array.from(values).forEach((entry) => {
        if (entry[1].defaultAdd) {
          this.addItem(entry[0])
        }
      })
    }
    return this
  }

  public clear() {
    localStorage.removeItem('cartInfo')
    this.data = {}
  }

  public upgrades = () => {
    const map = new Map<CartItemSet, UpgradeItemSet[]>()
    return map.get(this.getItemSet()) ?? []
  }

  public updateTotals() {
    this.data.margin = this.getMargin()
    this.data.total = this.getTotal()
    this.data.value = this.getValue()
  }

  public updateOffer(offerToUse?: OfferValue) {
    const offer = offerToUse ?? OfferValue.Trial

    this.removeItem(CartItemSlot.Subscription)
    this.removeItem(CartItemSlot.Trial)
    this.removeItem(CartItemSlot.SemiAnnual)

    switch (offer) {
      case OfferValue.Subscription:
        this.addItem(CartItemSlot.Subscription)
        break
      case OfferValue.Trial:
        this.addItem(CartItemSlot.Trial)
        break
      case OfferValue.SemiAnnual:
        this.addItem(CartItemSlot.SemiAnnual)
        break
      case OfferValue.Hidden:
      case OfferValue.None:
        break
      default:
        this.addItem(CartItemSlot.Trial)
        break
    }
    this.updateTotals()
    this.save()
  }

  public selectSet(cartItemSet: CartItemSet, reload?: boolean) {
    if (reload) {
      this.clear()
      this.data.itemSet = cartItemSet
      this.loadDefaultItems()
      this.save()
    } else {
      this.data.itemSet = cartItemSet
    }
  }

  public getSetItems() {
    const localItemsJSON = localStorage.getItem('cartItems')
    const itemSet = CartItems.get(this.getItemSet()) ?? Cart.defaultItems()
    if (localItemsJSON) {
      try {
        const localItemsArray: [[CartItemSlot, CartItem]] = JSON.parse(localItemsJSON)
        localItemsArray.forEach(([id, value]) => {
          itemSet.set(id, value)
        })
      } catch (ex) {
        // fail, but nicely
      }
    }
    return itemSet
  }

  public getItemsBySlot(slot: CartItemSlot) {
    return this.items.get(slot)
  }

  public getItems() {
    return this.data.items
  }

  public shippingRequired() {
    return this.itemsToShip().length > 0
  }
  // Kept this here for minimal refactor of code in other projects
  public updateShipping() {
    if (this.shippingRequired()) {
      this.addItem(CartItemSlot.Shipping)
      // Flat rate shipping for all customers
      // this.removeItem(CartItemSlot.ShippingIntl)
      // this.removeItem(CartItemSlot.Shipping)
      // if (country === 'US') {
      //   this.removeItem(CartItemSlot.ShippingIntl)
      //   this.removeItem(CartItemSlot.Shipping)
      //
      // } else {
      //   this.removeItem(CartItemSlot.Shipping)
      //   this.removeItem(CartItemSlot.ShippingIntl)
      //   this.addItem(CartItemSlot.ShippingIntl)
      // }
      this.save()
    }
  }

  public itemsToShip() {
    return (this.getItems() ?? []).filter((item) => item.ship)
  }

  public getShipping() {
    return this.items.get(CartItemSlot.Shipping)
  }

  public getShippingTotal() {
    const shipping = this.getShipping()
    return shipping?.price ?? 0
  }

  public setDiscount(code?: string) {
    if (code) {
      this.data.discountCode = DiscountCodes.get(code)
    } else {
      this.data.discountCode = undefined
    }
  }

  public addItem(slot: CartItemSlot) {
    const itemSet = CartItems.get(this.getItemSet())
    if (itemSet) {
      const item = itemSet.get(slot)
      if (item) {
        this.items.set(slot, item)
        if (item.defaultDiscount) {
          this.setDiscount(item.defaultDiscount)
        }
      } else {
        throw Error('addItem: Item missing from set')
      }
    } else {
      throw Error('addItem: No set selected')
    }
    this.refreshDataItems()
    this.updateTotals()
  }

  public removeItem(slot: CartItemSlot) {
    this.items.delete(slot)
    this.refreshDataItems()
    this.updateTotals()
  }

  public getTotal() {
    this.data.total = 0
    for (const item of this.getItems() ?? []) {
      this.data.total = this.data.total + (item.price ?? 0)
    }
    this.data.total = this.data.total + this.getDiscountAmount()
    this.data.total = Math.round(this.data.total * (1 + this.getDiscountPercent()) * 100) / 100
    return this.data.total
  }

  public getFormattedTotal() {
    return this.getTotal().toFixed(2)
  }

  public getSubTotal() {
    return this.getTotal() - this.getShippingTotal()
  }

  public getMsrp() {
    this.data.msrp = 0
    for (const item of this.getItems() ?? []) {
      this.data.msrp = this.data.msrp + (item.msrp ?? 0)
    }
    return this.data.msrp
  }

  public getFormattedMsrp() {
    return this.getMsrp().toFixed(2)
  }

  public getSavingsPercent() {
    const msrp = this.getMsrp()
    const total = this.getTotal()
    if (msrp === 0) return 0
    return 1 - total / msrp
  }

  public getMargin() {
    this.data.margin = 0
    for (const item of this.getItems() ?? []) {
      this.data.margin = this.data.margin + (item.price ?? 0) - (item.COGS ?? 0)
    }

    this.data.margin = this.data.margin + this.getDiscountAmount()
    this.data.margin = Math.ceil(this.data.margin * (1 + this.getDiscountPercent()) * 100) / 100

    return this.data.margin
  }

  public getDiscountAmount() {
    return this.data.discountCode?.amount ?? 0
  }

  public getDiscountPercent() {
    return this.data.discountCode?.percent ?? 0
  }

  public getValue() {
    this.data.value = 0
    for (const item of this.getItems() ?? []) {
      this.data.value = this.data.value + (item.value ?? 0)
    }

    this.data.value = this.data.value + this.getDiscountAmount()
    this.data.value = Math.round(this.data.value * (1 + this.getDiscountPercent()) * 100) / 100
    return this.data.value
  }

  /**
   *  @remmarks
   *  This method replace items in the CartItemSet, the cart.items and refreshes the cart.data.items
   * @param {CartItemSlot} slot - The slot to be filled
   * @param {CartItem} item - The item to fill the slot
   */
  public addCartItem(slot: CartItemSlot, item: CartItem) {
    CartItems.get(this.getItemSet())?.set(slot, item)
    this.removeItem(slot)
    this.addItem(slot)
    this.saveCartItems()
    this.refreshDataItems()
  }

  saveCartItems() {
    const saveItems = Array.from(this.items)
    localStorage.setItem('cartItems', JSON.stringify(saveItems))
  }

  static instance: Cart
  static get() {
    this.instance = this.instance ?? new Cart()
    return this.instance
  }
}
